import Vue from 'vue'
import { isNativeAndroid } from './nativeLocation'
import { getStorageJson, setStorageJson } from './storage'

const MAX_POSITION_AGE = 30000
const REQUEST_TIMEOUT = 45000
export const terminalPosition = Vue.observable({
  enabled: getStorageJson('terminalLocation', {}).enabled === true,
  nativeAndroid: isNativeAndroid(),
  position: null,
  error: '',
  permissionRequested: false,
  requesting: false
})
let watchId = null
let generation = 0
let initialized = false
let expiry = null
let requestTimer = null
let pendingRequest = null

function currentCoordinates () {
  const p = terminalPosition.position
  return isNativeAndroid() && terminalPosition.enabled && isValidPosition(p)
    ? { latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy, sitename: '终端设备位置' } : null
}

function isValidPosition (p) {
  if (!p || !p.coords || !Number.isFinite(p.timestamp)) return false
  const { latitude, longitude, accuracy } = p.coords
  const age = Date.now() - p.timestamp
  return [latitude, longitude, accuracy].every(Number.isFinite) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180 && accuracy >= 0 && age >= -5000 && age < MAX_POSITION_AGE
}

function finishRequest (coordinates = null) {
  const request = pendingRequest
  pendingRequest = null
  clearTimeout(requestTimer)
  terminalPosition.permissionRequested = false
  terminalPosition.requesting = false
  if (request) request.resolve(coordinates)
}

function clearPositionWatch () {
  generation++
  if (watchId != null && navigator.geolocation) navigator.geolocation.clearWatch(watchId)
  watchId = null
  clearTimeout(expiry)
  terminalPosition.position = null
}

export function stopTerminalPosition () {
  clearPositionWatch()
  finishRequest()
}

export function startTerminalPosition () {
  terminalPosition.nativeAndroid = isNativeAndroid()
  if (!terminalPosition.nativeAndroid || !terminalPosition.enabled || document.hidden || watchId != null) return
  if (!navigator.geolocation) {
    terminalPosition.error = '当前设备定位不可用，请确认 App 定位插件和系统定位已开启'
    finishRequest()
    return
  }
  const current = ++generation
  terminalPosition.error = ''
  const fail = error => {
    if (current !== generation) return
    terminalPosition.position = null
    terminalPosition.error = error.code === 1 ? '定位权限未授予，请在系统设置中允许位置权限后重新开启' : '暂时无法定位，请检查系统定位并到开阔处重试'
    finishRequest()
    if (error.code === 1) setTerminalPositionEnabled(false)
  }
  try {
    const id = navigator.geolocation.watchPosition(p => {
      if (current !== generation || !isNativeAndroid() || !terminalPosition.enabled || !isValidPosition(p)) return
      terminalPosition.position = p
      clearTimeout(expiry)
      expiry = setTimeout(() => { terminalPosition.position = null }, Math.max(0, MAX_POSITION_AGE - (Date.now() - p.timestamp)))
      finishRequest(currentCoordinates())
    }, fail, { enableHighAccuracy: true, timeout: 15000, maximumAge: 3000 })
    // 同步失败或关闭后的迟到回调不能重新挂回已作废的监听。
    if (current === generation) watchId = id
    else navigator.geolocation.clearWatch(id)
  } catch (error) {
    fail(error)
  }
}

export function setTerminalPositionEnabled (enabled) {
  if (enabled && !isNativeAndroid()) return
  terminalPosition.enabled = Boolean(enabled)
  setStorageJson('terminalLocation', { enabled: terminalPosition.enabled })
  clearPositionWatch()
  if (enabled) startTerminalPosition()
  else finishRequest()
}

function waitForCoordinates (enable = false) {
  terminalPosition.permissionRequested = false
  terminalPosition.requesting = true
  requestTimer = setTimeout(() => {
    terminalPosition.error = '获取终端位置超时，请检查系统定位并到开阔处重试'
    finishRequest()
  }, REQUEST_TIMEOUT)
  // 上一次失败后，允许点击入口重新请求，而不是只等待失效的监听。
  if (terminalPosition.error) clearPositionWatch()
  if (enable) setTerminalPositionEnabled(true)
  else startTerminalPosition()
}

export function requestTerminalCoordinates () {
  initTerminalPosition()
  if (!isNativeAndroid() || document.hidden) return Promise.resolve(null)
  if (pendingRequest) return pendingRequest.promise
  const coordinates = currentCoordinates()
  if (coordinates) return Promise.resolve(coordinates)
  let complete
  const promise = new Promise(resolve => { complete = resolve })
  pendingRequest = { promise, resolve: complete }
  if (terminalPosition.enabled) waitForCoordinates()
  else {
    terminalPosition.error = ''
    terminalPosition.permissionRequested = true
  }
  return promise
}

export function confirmTerminalLocationPermission () {
  if (!pendingRequest || !terminalPosition.permissionRequested || !isNativeAndroid() || document.hidden) {
    finishRequest()
    return
  }
  waitForCoordinates(true)
}

export function cancelTerminalLocationRequest () { finishRequest() }

export function initTerminalPosition () {
  terminalPosition.nativeAndroid = isNativeAndroid()
  if (initialized) return
  initialized = true
  document.addEventListener('deviceready', startTerminalPosition)
  document.addEventListener('pause', stopTerminalPosition)
  document.addEventListener('resume', startTerminalPosition)
  document.addEventListener('visibilitychange', () => document.hidden ? stopTerminalPosition() : startTerminalPosition())
  startTerminalPosition()
}

export const terminalPositionMixin = {
  computed: {
    isTerminalLocationAvailable () { return terminalPosition.nativeAndroid && isNativeAndroid() },
    terminalCoordinates () { return this.isTerminalLocationAvailable ? currentCoordinates() : null }
  },
  mounted () { initTerminalPosition() }
}
